import { useState, useEffect } from 'react'
import type { Card, Comentario, Coluna } from '../types'
import { CATEGORIAS, PRIORIDADES } from '../types'
import { api } from '../api'

interface Props {
  card: Card | null
  colunas: Coluna[]
  boardId: number
  defaultColuna?: string
  onClose: () => void
  onSaved: () => void
}

export default function CardModal({ card, colunas, boardId, defaultColuna, onClose, onSaved }: Props) {
  const isNew = !card
  const [titulo, setTitulo] = useState(card?.titulo ?? '')
  const [descricao, setDescricao] = useState(card?.descricao ?? '')
  const [categoria, setCategoria] = useState(card?.categoria ?? 'geral')
  const [prioridade, setPrioridade] = useState(card?.prioridade ?? 'media')
  const [tipo, setTipo] = useState(card?.tipo ?? 'tarefa')
  const [dataLimite, setDataLimite] = useState(card?.data_limite ?? '')
  const [tags, setTags] = useState(card?.tags.join(', ') ?? '')
  const [colunaId, setColunaId] = useState(card?.coluna_id ?? defaultColuna ?? 'ideia')
  const [comentarios, setComentarios] = useState<Comentario[]>([])
  const [novoComentario, setNovoComentario] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (card) {
      api.getCard(card.id).then((c) => setComentarios(c.comentarios))
    }
  }, [card])

  const handleSave = async () => {
    if (!titulo.trim()) return
    setSaving(true)
    try {
      const data = {
        titulo: titulo.trim(),
        descricao: descricao.trim() || undefined,
        categoria,
        prioridade,
        tipo,
        coluna_id: colunaId,
        data_limite: dataLimite || undefined,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      }
      if (isNew) {
        await api.createCard(boardId, data)
      } else {
        await api.updateCard(card.id, data)
      }
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const handleAddComment = async () => {
    if (!novoComentario.trim() || !card) return
    const c = await api.addComment(card.id, novoComentario.trim())
    setComentarios([...comentarios, c])
    setNovoComentario('')
  }

  const handleArchive = async () => {
    if (!card) return
    await api.archiveCard(card.id)
    onSaved()
  }

  const handleDelete = async () => {
    if (!card || !confirm('Excluir este card permanentemente?')) return
    await api.deleteCard(card.id)
    onSaved()
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 mb-12" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {isNew ? 'Novo Card' : 'Editar Card'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Titulo */}
          <input
            autoFocus
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Titulo do card..."
            className="w-full text-lg font-medium border-b border-gray-200 pb-2 focus:outline-none focus:border-blue-500"
          />

          {/* Descricao */}
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descricao detalhada (markdown)..."
            rows={4}
            className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          />

          {/* Grid de campos */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Coluna</label>
              <select value={colunaId} onChange={(e) => setColunaId(e.target.value)}
                className="w-full text-sm border rounded-lg p-2">
                {colunas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Categoria</label>
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                className="w-full text-sm border rounded-lg p-2">
                {CATEGORIAS.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Prioridade</label>
              <select value={prioridade} onChange={(e) => setPrioridade(e.target.value as Card['prioridade'])}
                className="w-full text-sm border rounded-lg p-2">
                {Object.entries(PRIORIDADES).map(([k, v]) => <option key={k} value={k}>{v.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value as Card['tipo'])}
                className="w-full text-sm border rounded-lg p-2">
                <option value="tarefa">Tarefa</option>
                <option value="ideia">Ideia</option>
                <option value="pesquisa">Pesquisa</option>
                <option value="decisao">Decisao</option>
                <option value="milestone">Milestone</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Data Limite</label>
              <input type="date" value={dataLimite} onChange={(e) => setDataLimite(e.target.value)}
                className="w-full text-sm border rounded-lg p-2" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Tags (separar por virgula)</label>
              <input value={tags} onChange={(e) => setTags(e.target.value)}
                placeholder="whatsapp, margem, seo"
                className="w-full text-sm border rounded-lg p-2" />
            </div>
          </div>

          {/* Comentarios (só se editando) */}
          {!isNew && (
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Atividade</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                {comentarios.map((c) => (
                  <div key={c.id} className={`text-sm p-2 rounded ${
                    c.tipo === 'movimentacao' ? 'bg-blue-50 text-blue-700' :
                    c.tipo === 'ia_sugestao' ? 'bg-purple-50 text-purple-700' :
                    'bg-gray-50 text-gray-700'
                  }`}>
                    <span className="font-medium">{c.autor}</span>
                    <span className="text-xs text-gray-400 ml-2">
                      {new Date(c.criado_em).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <p className="mt-0.5">{c.conteudo}</p>
                  </div>
                ))}
                {comentarios.length === 0 && <p className="text-xs text-gray-400">Nenhuma atividade</p>}
              </div>
              <div className="flex gap-2">
                <input
                  value={novoComentario}
                  onChange={(e) => setNovoComentario(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  placeholder="Adicionar comentario..."
                  className="flex-1 text-sm border rounded-lg p-2"
                />
                <button onClick={handleAddComment}
                  className="text-sm bg-blue-600 text-white px-3 rounded-lg hover:bg-blue-700">
                  Enviar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <div className="flex gap-2">
            {!isNew && (
              <>
                <button onClick={handleArchive} className="text-xs text-gray-500 hover:text-yellow-600">
                  {card.arquivado ? 'Desarquivar' : 'Arquivar'}
                </button>
                <button onClick={handleDelete} className="text-xs text-gray-500 hover:text-red-600">Excluir</button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 border rounded-lg hover:bg-gray-100">Cancelar</button>
            <button onClick={handleSave} disabled={saving || !titulo.trim()}
              className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Salvando...' : isNew ? 'Criar' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
